import { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import styled, { css } from 'styled-components';
import { StatusTone } from './utils';

/**
 * The card language shared by the restaurant dashboard (cards.tsx) and the multi-restaurant home view
 * (../RestaurantCardView.tsx): the card surface, the status pill and the stat label/value pair. Kept
 * here so the two pages cannot drift apart in the parts a user sees side by side.
 */

export const toneColor = (theme: Theme, tone: StatusTone): string => {
  switch (tone) {
    case 'open':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    default:
      return theme.palette.text.secondary;
  }
};

// At or below this width the page's own gutter costs more than it gives, so the cards cancel it and
// run edge to edge — square corners and no side borders, since a rounded box and a hairline sitting
// on the viewport edge just read as clutter. Their own padding still keeps the contents off the
// screen edge. The negative margin mirrors the content padding in layout/Main.
const FULL_BLEED_MAX_WIDTH = '375px';

export const cardStyles = css`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: ${({ theme }) => theme.spacing(2)};
  box-sizing: border-box;

  @media (max-width: ${FULL_BLEED_MAX_WIDTH}) {
    margin-inline: calc(-1 * ${({ theme }) => theme.spacing(3)});
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;

export const Card = styled.div`
  ${cardStyles}
`;

// Shrinks below the sm breakpoint so a long status label (e.g. "Ei määriteltyä tilaa") and the menu
// chip still fit side by side on ~320px phones instead of overflowing the viewport.
export const StatusPill = styled.div<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.md};
  white-space: nowrap;
  color: ${({ theme, $tone }) => toneColor(theme, $tone)};
  background-color: ${({ theme, $tone }) => alpha(toneColor(theme, $tone), 0.14)};

  ${({ theme }) => theme.breakpoints.down('sm')} {
    gap: 6px;
    padding: 6px 10px;
  }
`;

export const PillIcon = styled.span`
  display: flex;
  font-size: 20px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: 18px;
  }
`;

export const PillDetail = styled.span`
  font-weight: 400;
  opacity: 0.85;
`;

export const StatLabel = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: 600;
`;

export const StatValue = styled.div`
  margin-top: 2px;
  font-size: clamp(${({ theme }) => theme.fontSizes.lg}, 5vw, ${({ theme }) => theme.fontSizes.xl});
  font-weight: 700;
  overflow-wrap: anywhere;
`;
